/**
 * @fileoverview Star rating input component
 * @module ctl-rating
 * @version 1.0.0
 * 
 * @description
 * A star rating component that allows users to rate items on a scale (typically 1-5 stars).
 * Supports full stars, half stars, read-only display mode, and interactive rating selection.
 * Provides visual feedback on hover and supports keyboard navigation for accessibility.
 * 
 * The component can be used for collecting user ratings (interactive mode) or displaying
 * existing ratings (read-only mode). It fires custom events when the rating changes,
 * making it easy to integrate with form handling and data collection.
 * 
 * @example
 * <!-- Basic 5-star rating -->
 * <ctl-rating value="3"></ctl-rating>
 * 
 * @example
 * <!-- Rating with half stars -->
 * <ctl-rating value="3.5" max="5" allow-half></ctl-rating>
 * 
 * @example
 * <!-- Read-only rating display -->
 * <ctl-rating value="4.2" readonly></ctl-rating>
 * 
 * @example
 * <!-- Custom max stars and name for forms -->
 * <ctl-rating 
 *   name="product-rating" 
 *   max="10" 
 *   value="7">
 * </ctl-rating>
 * 
 * @example
 * <!-- Disabled rating -->
 * <ctl-rating value="3" disabled></ctl-rating>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui,-apple-system,Segoe UI,Roboto,Arial;
    --ctl-color-focus: var(--ctl-color-brand);
    --ctl-rating-size: var(--ctl-size-icon-lg);

    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1);
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  :host([disabled]) {
    opacity: var(--ctl-opacity-muted);
    cursor: not-allowed;
  }

  .rating-container {
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1);
  }

  .star {
    width: var(--ctl-rating-size);
    height: var(--ctl-rating-size);
    cursor: pointer;
    transition: transform var(--ctl-transition-fast), color var(--ctl-transition-fast);
    color: var(--ctl-color-muted);
  }

  :host(:not([disabled])) .star:hover {
    transform: scale(1.1);
  }

  .star.filled {
    color: var(--ctl-color-rating);
  }

  .star.half-filled {
    position: relative;
    color: var(--ctl-color-muted);
  }

  .star.half-filled::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 50%;
    height: 100%;
    overflow: hidden;
    color: var(--ctl-color-rating);
  }

  :host([disabled]) .star {
    cursor: not-allowed;
  }

  .star svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .label {
    margin-left: var(--ctl-space-2);
    font-size: var(--ctl-font-size-base);
    color: var(--ctl-color-text);
  }

  :host([readonly]) .star {
    cursor: default;
  }

  :host([readonly]) .star:hover {
    transform: none;
  }

  :host([invalid]) .star {
    color: var(--ctl-color-danger, #dc2626);
  }

  :host([invalid]) .star.filled {
    color: var(--ctl-color-danger, #dc2626);
  }

  input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
    pointer-events: none;
  }

  input[type="radio"]:focus-visible ~ .star {
    outline: var(--ctl-border-width-thick) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
    border-radius: var(--ctl-radius-sm);
  }
</style>

<div class="rating-container" part="container" id="container">
  <!-- Stars will be rendered here -->
</div>
<span class="label" part="label">
  <slot></slot>
</span>
`;

/**
 * Star Rating Web Component
 * 
 * A star rating component for collecting or displaying ratings on a numeric scale.
 * Supports full and half stars, interactive and read-only modes.
 * 
 * @class CtlRating
 * @extends HTMLElement
 * 
 * @attr {number} value - Current rating value (supports decimals for half stars)
 * @attr {number} max - Maximum number of stars (default: 5)
 * @attr {boolean} disabled - Whether rating is disabled
 * @attr {boolean} readonly - Display rating without allowing changes
 * @attr {boolean} required - Whether rating is required
 * @attr {boolean} invalid - Whether rating is in invalid state
 * @attr {string} name - Form field name for submission
 * @attr {boolean} allow-half - Allow half-star ratings
 * @attr {boolean} show-value - Display numeric value next to stars
 * @attr {string} aria-label - Accessible label
 * @attr {string} aria-labelledby - ID of labeling element
 * @attr {string} aria-describedby - ID of describing element
 * @attr {boolean} aria-invalid - ARIA invalid state
 * 
 * @fires ctl-change - Custom event dispatched when the rating value changes (detail.value is the new rating)
 * 
 * @cssprop --ctl-font - Font family (default: system-ui,-apple-system,Segoe UI,Roboto,Arial)
 * @cssprop --ctl-color-text - Text color (default: #111827)
 * @cssprop --ctl-color-muted - Unfilled star color (default: #e5e7eb)
 * @cssprop --ctl-color-focus - Focus ring color (default: #4f46e5)
 * @cssprop --ctl-color-rating - Filled star color (default: #fbbf24)
 * @cssprop --ctl-rating-size - Size of each star (default: 24px)
 */
export class CtlRating extends HTMLElement {
  /**
   * Observed HTML attributes that trigger attributeChangedCallback
   * @returns {string[]} Array of attribute names to observe
   */
  static get observedAttributes() {
    return [
      "value",
      "max",
      "disabled",
      "readonly",
      "required",
      "invalid",
      "name",
      "allow-half",
      "show-value",
      "aria-label",
      "aria-labelledby",
      "aria-describedby",
      "aria-invalid"
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._container = this.shadowRoot.getElementById("container");
    this._hoverValue = null;
    // Generate unique ID for gradient to avoid conflicts between multiple instances
    this._uniqueId = `rating-${Math.random().toString(36).substr(2, 9)}`;
  }

  connectedCallback() {
    this._upgradeProperty("value");
    this._upgradeProperty("max");
    this._upgradeProperty("disabled");
    this._upgradeProperty("name");

    this._render();
  }

  disconnectedCallback() {
    // Cleanup event listeners
    this._removeEventListeners();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    this._render();
  }

  // --- Properties ---
  get value() {
    return parseFloat(this.getAttribute("value")) || 0;
  }

  set value(val) {
    const num = parseFloat(val);
    if (!isNaN(num)) {
      const max = this.max;
      const clamped = Math.max(0, Math.min(max, num));
      this.setAttribute("value", String(clamped));
    }
  }

  get max() {
    return parseInt(this.getAttribute("max")) || 5;
  }

  set max(val) {
    const num = parseInt(val);
    if (!isNaN(num) && num > 0) {
      this.setAttribute("max", String(num));
    }
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(value) {
    if (value) this.setAttribute("disabled", "");
    else this.removeAttribute("disabled");
  }

  get readonly() {
    return this.hasAttribute("readonly");
  }

  set readonly(value) {
    if (value) this.setAttribute("readonly", "");
    else this.removeAttribute("readonly");
  }

  get required() {
    return this.hasAttribute("required");
  }

  set required(value) {
    if (value) this.setAttribute("required", "");
    else this.removeAttribute("required");
  }

  get name() {
    return this.getAttribute("name");
  }

  set name(val) {
    if (val == null) this.removeAttribute("name");
    else this.setAttribute("name", String(val));
  }

  // --- Internals ---
  _upgradeProperty(prop) {
    if (Object.prototype.hasOwnProperty.call(this, prop)) {
      const value = this[prop];
      delete this[prop];
      this[prop] = value;
    }
  }

  _removeEventListeners() {
    const stars = this._container.querySelectorAll(".star");
    stars.forEach((star) => {
      star.removeEventListener("click", star._clickHandler);
      star.removeEventListener("mouseenter", star._mouseenterHandler);
      star.removeEventListener("mouseleave", star._mouseleaveHandler);
    });
  }

  _render() {
    const max = this.max;
    const value = this.value;
    const disabled = this.hasAttribute("disabled");
    const readonly = this.hasAttribute("readonly");
    const allowHalf = this.hasAttribute("allow-half");
    const showValue = this.hasAttribute("show-value");

    // Clear existing stars
    this._removeEventListeners();
    this._container.innerHTML = "";

    // Create stars
    for (let i = 1; i <= max; i++) {
      const star = document.createElement("span");
      star.className = "star";
      star.setAttribute("data-value", String(i));
      star.part = "star";

      // Determine if star should be filled, half-filled, or empty
      if (allowHalf) {
        if (i <= Math.floor(value)) {
          star.classList.add("filled");
        } else if (i === Math.ceil(value) && value % 1 !== 0) {
          // Half star - use unique ID to avoid conflicts between multiple rating instances
          const gradientId = `${this._uniqueId}-half-${i}`;
          star.innerHTML = `
            <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="${gradientId}">
                  <stop offset="50%" stop-color="var(--ctl-color-rating, #fbbf24)" />
                  <stop offset="50%" stop-color="var(--ctl-color-muted, #e5e7eb)" />
                </linearGradient>
              </defs>
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="url(#${gradientId})" />
            </svg>
          `;
        } else {
          star.innerHTML = `
            <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
          `;
        }
      } else {
        if (i <= value) {
          star.classList.add("filled");
        }
        star.innerHTML = `
          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
          </svg>
        `;
      }

      // Add event listeners if not disabled and not readonly
      if (!disabled && !readonly) {
        star._clickHandler = () => this._handleClick(i);
        star._mouseenterHandler = () => this._handleMouseEnter(i);
        star._mouseleaveHandler = () => this._handleMouseLeave();

        star.addEventListener("click", star._clickHandler);
        star.addEventListener("mouseenter", star._mouseenterHandler);
        star.addEventListener("mouseleave", star._mouseleaveHandler);
      }

      this._container.appendChild(star);
    }

    // Add value display if requested
    if (showValue) {
      const valueDisplay = document.createElement("span");
      valueDisplay.className = "label";
      valueDisplay.textContent = `${value} / ${max}`;
      this._container.appendChild(valueDisplay);
    }
  }

  _handleClick(starValue) {
    if (this.hasAttribute("disabled") || this.hasAttribute("readonly")) return;

    this.value = starValue;

    // Emit custom event
    this._emit("ctl-change", {
      name: this.getAttribute("name") || null,
      value: this.value,
      originalEvent: null
    });
  }

  _handleMouseEnter(starValue) {
    if (this.hasAttribute("disabled") || this.hasAttribute("readonly")) return;

    this._hoverValue = starValue;
    this._updateHoverState();
  }

  _handleMouseLeave() {
    if (this.hasAttribute("disabled") || this.hasAttribute("readonly")) return;

    this._hoverValue = null;
    this._updateHoverState();
  }

  _updateHoverState() {
    const stars = this._container.querySelectorAll(".star");
    const displayValue = this._hoverValue !== null ? this._hoverValue : this.value;
    const allowHalf = this.hasAttribute("allow-half");

    // If hovering with half-star support, re-render to show proper half-stars
    if (this._hoverValue !== null && allowHalf) {
      // Temporarily set value for rendering
      const originalValue = this.value;
      this._value = this._hoverValue;
      this._render();
      this._value = originalValue;
    } else {
      // Simple filled/unfilled toggle for non-half-star hover
      stars.forEach((star, index) => {
        const starValue = index + 1;
        if (starValue <= displayValue) {
          star.classList.add("filled");
        } else {
          star.classList.remove("filled");
        }
      });
    }
  }

  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail
      })
    );
  }
}

customElements.define("ctl-rating", CtlRating);
